import { useCallback, useEffect, useMemo, useState } from 'react';
import useSession from '#/common/hooks/useSession';

/**
 * Manages the dashboard organisation filter.
 *
 * - Only relevant when the user belongs to more than one organisation.
 * - The URL `?org=<id>` is the single source of truth: present → select that org,
 *   absent/cleared → "All Organizations".
 * - Returns `tenantIDs` as a single-element array (or undefined for "All")
 *   ready to be passed straight to `useSingleStatsQuery`.
 */
const useDashboardOrgFilter = (initialOrgIdFromUrl?: string) => {
  const { user, tenants } = useSession();

  const organizations = useMemo(
    () =>
      (tenants ?? []).map((t) => ({
        id: t.tenant.id,
        name: t.tenant.name ?? t.tenant.id,
      })),
    [tenants],
  );

  const isMultiOrg = organizations.length > 1;

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);

  // URL is the single source of truth:
  // - URL has ?org=<id>  → select that org
  // - URL cleared / absent → "All Organizations" (null)
  // localStorage is only written (not read back) so the URL always wins.
  useEffect(() => {
    if (!isMultiOrg || organizations.length === 0) return;
    if (initialOrgIdFromUrl && organizations.some((o) => o.id === initialOrgIdFromUrl)) {
      setSelectedOrgId(initialOrgIdFromUrl);
    } else {
      setSelectedOrgId(null);
    }
  }, [isMultiOrg, organizations.length, initialOrgIdFromUrl, user?.id]);

  const persistOrgId = useCallback((id: string | null) => {
    setSelectedOrgId(id);
  }, []);

  /** Pass this directly to useSingleStatsQuery as the tenantIDs option. */
  const tenantIDs = useMemo<string[] | undefined>(
    () => (selectedOrgId ? [selectedOrgId] : undefined),
    [selectedOrgId],
  );

  const selectedOrg = useMemo<{ id: string; name: string } | null>(() => {
    if (!selectedOrgId) return null;
    return organizations?.find((t) => t.id === selectedOrgId) ?? null;
  }, [selectedOrgId, organizations]);

  return {
    /** True when the user belongs to more than one organisation. */
    isMultiOrg,
    /** All organizations the user belongs to. */
    organizations,
    /** Currently selected org ID, or null for "All Organizations". */
    selectedOrgId,
    selectedOrg,
    /** Update the selected org (URL is the source of truth — call handleOrgChange in the page to sync URL). */
    setSelectedOrgId: persistOrgId,
    /** tenantIDs array ready for useSingleStatsQuery, or undefined for all. */
    tenantIDs,
  };
};

export default useDashboardOrgFilter;
